// src/components/Navbar.jsx

import React, { useEffect, useState, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  MenuItem,
  Divider,
  Paper,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../services/authService";
import { fetchCurrentUser, clearUser } from "../userSlice";
import { switchOrganization, getUserOrganizations } from "../services/authService";
import { updateCurrentOrganization } from "../userSlice";

import RoomIcon from "@mui/icons-material/MeetingRoom";
import SettingsIcon from "@mui/icons-material/Settings";
import GroupIcon from "@mui/icons-material/Group";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";

// Import the logo and icons
import MeetingLogo from "../assets/Meet-ing-logo.jpg";
import ChevronDownIcon from "../assets/chevron-down.svg";

function Navbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user } = useSelector((state) => state.user);

  // State management
  const [mobileOpen, setMobileOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userMenuPosition, setUserMenuPosition] = useState({ top: 0, left: 0 });
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [orgMenuPosition, setOrgMenuPosition] = useState({ top: 0, left: 0 });
  const [orgLoading, setOrgLoading] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [orgHoverTimeout, setOrgHoverTimeout] = useState(null);
  const [orgSectionExpanded, setOrgSectionExpanded] = useState(false);

  // Responsive breakpoint
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // All hooks must be declared before any early returns
  const fetchOrganizations = useCallback(async () => {
    try {
      setOrgLoading(true);
      const orgs = await getUserOrganizations();
      setOrganizations(Array.isArray(orgs) ? orgs : []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setOrganizations([]);
    } finally {
      setOrgLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(fetchCurrentUser());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      fetchOrganizations();
    }
  }, [user, fetchOrganizations]);

  // Routes where navbar should be hidden
  const hiddenNavbarRoutes = [
    '/login',
    '/register',
    '/organization-choice',
    '/join-pending',
    '/forgot-password',
    '/reset-password'
  ];

  const shouldHideNavbar = () => {
    const currentPath = location.pathname;
    
    // Check exact matches
    if (hiddenNavbarRoutes.includes(currentPath)) {
      return true;
    }
    
    // Check pattern matches
    if (currentPath.startsWith('/join-org/') || 
        currentPath.startsWith('/onboarding/') ||
        currentPath.startsWith('/invite/')) {
      return true;
    }
    
    return false;
  };

  function isOrgAdmin() {
    if (!user || !user.currentOrganization || !user.currentOrganization.users) return false;
    const entry = user.currentOrganization.users.find(
      (e) => e.user && e.user._id && e.user._id === user._id
    );
    return entry ? entry.role === "admin" : false;
  }

  // Helper functions for user information
  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (user.name) return user.name;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    if (user.username) return user.username;
    if (user.email) return user.email.split('@')[0]; // Use email prefix as fallback
    return 'User';
  };

  const getUserAvatar = () => {
    if (!user) return null;
    return user.avatarUrl || user.avatar?.url || user.avatar?.thumbnailUrl || null;
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const displayName = getUserDisplayName();
    if (displayName === 'User' && user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return displayName.charAt(0).toUpperCase();
  };

  // Don't render navbar if it should be hidden (after all hooks are declared)
  if (shouldHideNavbar()) {
    return null;
  }

  const handleOrganizationChange = async (event) => {
    const organizationId = event.target.value;
    if (organizationId === user.currentOrganization?._id) return;

    try {
      setOrgLoading(true);
      const response = await switchOrganization(organizationId);
      
      dispatch(updateCurrentOrganization({
        organization: response.user.currentOrganization
      }));
      
      navigate("/rooms");
      window.location.reload();
    } catch (error) {
      console.error("Error switching organization:", error);
    } finally {
      setOrgLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      dispatch(clearUser());
      navigate("/login");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const handleUserMenuClick = (event) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    const dropdownWidth = 180;
    const viewportWidth = window.innerWidth;
    
    // Calculate left position ensuring dropdown stays in viewport
    let leftPosition = rect.left + (rect.width / 2) - (dropdownWidth / 2);
    
    // Adjust if dropdown would go off-screen
    if (leftPosition < 8) {
      leftPosition = 8;
    } else if (leftPosition + dropdownWidth > viewportWidth - 8) {
      leftPosition = viewportWidth - dropdownWidth - 8;
    }
    
    setUserMenuPosition({
      top: rect.bottom + 8,
      left: leftPosition,
    });
    setUserMenuOpen(true);
  };

  const handleUserMenuClose = () => {
    const timeout = setTimeout(() => {
      setUserMenuOpen(false);
    }, 100); // Small delay to prevent flicker when moving between elements
    setHoverTimeout(timeout);
  };

  const handleOrgMenuClick = (event) => {
    if (orgHoverTimeout) {
      clearTimeout(orgHoverTimeout);
      setOrgHoverTimeout(null);
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    const dropdownWidth = 180;
    const viewportWidth = window.innerWidth;
    
    // Calculate left position ensuring dropdown stays in viewport
    let leftPosition = rect.left;
    
    // Adjust if dropdown would go off-screen
    if (leftPosition + dropdownWidth > viewportWidth - 8) {
      leftPosition = viewportWidth - dropdownWidth - 8;
    }
    if (leftPosition < 8) {
      leftPosition = 8;
    }
    
    setOrgMenuPosition({
      top: rect.bottom + 8,
      left: leftPosition,
    });
    setOrgMenuOpen(true);
  };

  const handleOrgMenuClose = () => {
    const timeout = setTimeout(() => {
      setOrgMenuOpen(false);
    }, 100);
    setOrgHoverTimeout(timeout);
  };

  const isActivePage = (path) => {
    return location.pathname === path;
  };

  // Toggle for the mobile drawer
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Navigation menu items for desktop
  const navItems = [
    { label: "All Rooms", path: "/rooms", active: isActivePage("/rooms"), visible: true },
    { label: "My Booking", path: "/my-bookings", active: isActivePage("/my-bookings"), visible: true },
    { label: "All Booking", path: "/all-bookings", active: isActivePage("/all-bookings"), visible: isOrgAdmin() },
    { label: "Members", path: "/org-members", active: isActivePage("/org-members"), visible: isOrgAdmin() },
    { label: "Create a room", path: "/create-room", active: isActivePage("/create-room"), visible: isOrgAdmin() },
  ];

  // Build menu items for mobile drawer
  const MobileMenuItems = () => {
    if (!user) {
      return (
        <>
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/login" onClick={handleDrawerToggle}>
              <LoginIcon sx={{ mr: 1 }} />
              <ListItemText primary="Login" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/register" onClick={handleDrawerToggle}>
              <PersonAddIcon sx={{ mr: 1 }} />
              <ListItemText primary="Register" />
            </ListItemButton>
          </ListItem>
        </>
      );
    }

    return (
      <>
        {navItems.filter(item => item.visible).map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton component={Link} to={item.path} onClick={handleDrawerToggle}>
              {item.path === "/rooms" && <HomeIcon sx={{ mr: 1 }} />}
              {item.path === "/my-bookings" && <CalendarMonthIcon sx={{ mr: 1 }} />}
              {item.path === "/all-bookings" && <CalendarMonthIcon sx={{ mr: 1 }} />}
              {item.path === "/org-members" && <GroupIcon sx={{ mr: 1 }} />}
              {item.path === "/create-room" && <RoomIcon sx={{ mr: 1 }} />}
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/user-profile" onClick={handleDrawerToggle}>
            <PersonIcon sx={{ mr: 1 }} />
            <ListItemText primary="Profile" />
          </ListItemButton>
        </ListItem>
        {isOrgAdmin() && (
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/org-settings" onClick={handleDrawerToggle}>
              <SettingsIcon sx={{ mr: 1 }} />
              <ListItemText primary="Organization Settings" />
            </ListItemButton>
          </ListItem>
        )}
        {isOrgAdmin() && (
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/manage-rooms" onClick={handleDrawerToggle}>
              <SettingsIcon sx={{ mr: 1 }} />
              <ListItemText primary="Manage Rooms" />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemButton onClick={() => { handleDrawerToggle(); handleLogout(); }}>
            <LogoutIcon sx={{ mr: 1 }} />
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </>
    );
  };

  // Drawer content for mobile
  const drawer = (
    <Box sx={{ width: 250 }} onClick={isMobile ? handleDrawerToggle : undefined}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Box component="img" src={MeetingLogo} alt="Meeting logo" sx={{ width: 96, height: 35 }} />
      </Box>
      
      {/* User Info Section for Mobile */}
      {user && (
        <>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                src={getUserAvatar()}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "#ed5191",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                {getUserInitials()}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  {getUserDisplayName()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.currentOrganization?.name}
                </Typography>
              </Box>
            </Stack>
          </Box>
          

        </>
      )}
      
      <List sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <MobileMenuItems />
        
        {/* Collapsible Organization Section at Bottom */}
        {user && organizations.length > 1 && (
          <>
            <Divider sx={{ my: 1 }} />
            
            {/* Organization Section Toggle */}
            <ListItem 
              disablePadding
              onClick={(e) => {
                e.stopPropagation();
                setOrgSectionExpanded(!orgSectionExpanded);
              }}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemButton sx={{ py: 1 }}>
                <BusinessIcon sx={{ mr: 1 }} />
                <ListItemText 
                  primary={organizations.length > 1 ? "Organizations" : "Organization"}
                  secondary={user.currentOrganization?.name}
                />
                <ExpandMore 
                  sx={{ 
                    transform: orgSectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </ListItemButton>
            </ListItem>
            
            {/* Expandable Organization Content */}
            {orgSectionExpanded && (
              <Box sx={{ pl: 2, pr: 2, pb: 1 }}>
                {/* Current Organization */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'primary.light',
                    mb: organizations.length > 1 ? 1 : 0,
                    border: '1px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <Avatar
                    src={user.currentOrganization?.logo?.url || user.currentOrganization?.logo?.thumbnailUrl}
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      fontSize: '14px',
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.primary.main}`,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {user.currentOrganization?.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {user.currentOrganization?.name}
                    </Typography>
                    <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
                      Current Organization
                    </Typography>
                  </Box>
                </Box>

                {/* Other Organizations - Show if Multiple */}
                {organizations.length > 1 && (
                  <Stack spacing={0.5}>
                    {organizations
                      .filter(org => org._id !== user.currentOrganization._id)
                      .map((org) => (
                                                 <Box
                           key={org._id}
                           onClick={(e) => {
                             e.stopPropagation();
                             handleOrganizationChange({ target: { value: org._id } });
                             handleDrawerToggle();
                             setOrgSectionExpanded(false);
                           }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.5,
                            borderRadius: 1,
                            cursor: 'pointer',
                            border: '1px solid transparent',
                            '&:hover': { 
                              bgcolor: 'grey.100',
                              borderColor: 'grey.300',
                              transform: 'translateX(2px)',
                              transition: 'all 0.2s ease'
                            }
                          }}
                        >
                          <Avatar
                            src={org.logo?.url || org.logo?.thumbnailUrl}
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              fontSize: '14px',
                              bgcolor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            {org.name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {org.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Click to switch
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                  </Stack>
                )}
              </Box>
            )}
          </>
        )}
      </List>
    </Box>
  );

  // Organization Selector Component
  const OrganizationSelector = () => {
    if (!user || !user.currentOrganization) return null;
    
    const hasMultipleOrgs = organizations.length > 1;
    
    return (
      <Box
        onMouseEnter={hasMultipleOrgs ? handleOrgMenuClick : undefined}
        onMouseLeave={() => {}} 
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: hasMultipleOrgs ? 'pointer' : 'default',
          '&:hover': {
            opacity: hasMultipleOrgs ? 0.8 : 1
          }
        }}
      >
        <Avatar
          src={user.currentOrganization.logo?.url || user.currentOrganization.logo?.thumbnailUrl}
          sx={{
            width: 33,
            height: 33,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.primary.main}`,
            fontSize: "14px",
            color: theme.palette.primary.main,
          }}
        >
          {user.currentOrganization.name?.charAt(0).toUpperCase() || 'O'}
        </Avatar>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
            fontSize: "14px",
          }}
        >
          {user.currentOrganization.name}
        </Typography>
        {hasMultipleOrgs && (
          <Box
            component="img"
            src={ChevronDownIcon}
            alt="chevron down"
            sx={{
              width: 11,
              height: 6,
              filter: `brightness(0) saturate(100%) invert(64%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(85%) contrast(86%)`,
            }}
          />
        )}
      </Box>
    );
  };



  // User Dropdown Component
  const UserDropdown = () => {
    if (!user) return null;

    return (
      <Box
        onMouseEnter={handleUserMenuClick}
        onMouseLeave={() => {}} // Keep open until mouse leaves dropdown area
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8
          }
        }}
      >
        <Avatar
          src={getUserAvatar()}
          sx={{
            width: 33,
            height: 33,
            bgcolor: "#ed5191",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          {getUserInitials()}
        </Avatar>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
            fontSize: "14px",
          }}
        >
          {getUserDisplayName()}
        </Typography>
        <Box
          component="img"
          src={ChevronDownIcon}
          alt="chevron down"
          sx={{
            width: 11,
            height: 6,
            filter: `brightness(0) saturate(100%) invert(64%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(85%) contrast(86%)`,
          }}
        />
      </Box>
    );
  };

  if (!user) {
    return (
      <Box>
        <AppBar 
        position="fixed" 
        color="default" 
        elevation={0} 
        sx={{ 
           height: "73px", 
           zIndex: theme.zIndex.appBar,
           top: 0,
           left: 0,
           right: 0,
           backdropFilter: 'blur(8px)',
           transition: 'box-shadow 0.2s ease, backdrop-filter 0.2s ease',
           boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
         }}
      >
        <Toolbar sx={{ height: "100%", px: 2, py: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <Box component="img" src={MeetingLogo} alt="Meeting logo" sx={{ width: 128, height: 47 }} />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button component={Link} to="/login" variant="outlined">
              Login
            </Button>
            <Button component={Link} to="/register" variant="contained">
              Register
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Spacer to push content below fixed header */}
      <Box sx={{ height: "73px" }} />
    </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 0, position: 'relative' }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{ 
          height: "73px",
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: theme.zIndex.appBar,
          top: 0,
          left: 0,
          right: 0,
          backdropFilter: 'blur(8px)',
          transition: 'box-shadow 0.2s ease, backdrop-filter 0.2s ease',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ height: "100%", px: 2, py: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
              width: "100%",
              alignItems: "center",
              position: "relative",
            }}
          >
            {/* Left Section - Logo and Selectors */}
            <Box sx={{ 
              display: "flex", 
              justifyContent: "flex-start", 
              alignItems: "center",
              overflow: "hidden", // Prevent overflow
            }}>
              <Stack direction="row" spacing={{ lg: 2, xl: 4 }} alignItems="center" sx={{ minWidth: 0 }}>
                {/* Logo */}
                <Box component="img" src={MeetingLogo} alt="Meeting logo" sx={{ width: 128, height: 47, flexShrink: 0 }} />
                
                {/* Organization Selector - Large screens only */}
                <Box sx={{ display: { xs: "none", lg: "flex" }, minWidth: 0 }}>
                  <OrganizationSelector />
                </Box>

                {/* User Dropdown - Large screens only */}
                <Box sx={{ display: { xs: "none", lg: "flex" }, minWidth: 0 }}>
                  <UserDropdown />
                </Box>
              </Stack>
            </Box>

            {/* Center Section - Navigation Items (Grid column 2 - auto width for true centering) */}
            <Box sx={{ 
              display: { xs: "none", lg: "flex" }, 
              justifyContent: "center", 
              alignItems: "center",
              gap: { lg: 3, xl: 6 }, // Smaller gap on lg (1200-1536px), normal gap on xl (1536px+)
              whiteSpace: "nowrap", // Keep menu items on one line
            }}>
              {navItems.filter(item => item.visible).map((item) => (
                <Typography
                  key={item.path}
                  component={Link}
                  to={item.path}
                  variant="body2"
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "14px",
                    color: item.active ? theme.palette.text.primary : theme.palette.text.secondary,
                    cursor: "pointer",
                    textDecoration: "none",
                    fontWeight: item.active ? 500 : 400,
                    whiteSpace: "nowrap",
                    '&:hover': {
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>

            {/* Right Section - Book a Room Button or Mobile Menu */}
            <Box sx={{ 
              display: "flex", 
              justifyContent: "flex-end",
              alignItems: "center", 
              gap: { md: 1, lg: 1.5, xl: 2 }, // Responsive gap for right section
            }}>
              {/* Desktop Book a Room Button */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Button
                  variant="contained"
                  component={Link}
                  to="/book-room"
                  sx={{
                    width: { md: "140px", lg: "159px" },
                    height: "43px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "3px",
                    boxShadow: theme.shadows[3],
                    textTransform: "none",
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 500,
                    fontSize: { md: "13px", lg: "14px" },
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: theme.shadows[6],
                    },
                  }}
                >
                  Book a Room
                </Button>
              </Box>

              {/* Mobile actions: Book + Hamburger, side by side on the right */}
              <Box 
                sx={{ 
                  display: { xs: "flex", md: "none" },
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <Button
                  variant="contained"
                  component={Link}
                  to="/book-room"
                  size="small"
                  sx={{
                    minWidth: "auto",
                    px: 2,
                    py: 1,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "3px",
                    textTransform: "none",
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 500,
                    fontSize: "12px",
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  Book
                </Button>
                <IconButton 
                  onClick={handleDrawerToggle} 
                  sx={{ 
                    color: theme.palette.text.primary,
                    p: { xs: 1, sm: 1.5 }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Spacer to push content below fixed header */}
      <Box sx={{ height: "73px" }} />

      {/* Drawer for mobile */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
      >
        {drawer}
      </Drawer>

      {/* Custom User Dropdown */}
      {userMenuOpen && (
        <>
          {/* Backdrop */}
          <Box
            onClick={handleUserMenuClose}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: theme.zIndex.modal,
              background: 'transparent',
            }}
          />
          {/* Dropdown Menu */}
          <Paper
            elevation={1}
            onMouseEnter={() => {
              if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
              }
            }}
            onMouseLeave={handleUserMenuClose}
            sx={{
              position: 'fixed',
              top: `${userMenuPosition.top}px`,
              left: `${userMenuPosition.left}px`,
              width: "fit-content",
              p: '8px',
              borderRadius: 2,
              zIndex: theme.zIndex.modal + 1,
            }}
          >
                         <List sx={{ pl: '12px', pt: '4px', pb: '4px', minWidth: 180 }}>
                <ListItem
                  disablePadding
                  onClick={() => { handleUserMenuClose(); navigate('/user-profile'); }}
                  sx={{
                    py: 0.75,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <ListItemText
                    primary="Profile"
                    primaryTypographyProps={{
                      sx: {
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 15,
                        color: "#1a1a1a",
                        fontWeight: "normal",
                      },
                    }}
                  />
                </ListItem>
                <Divider sx={{ bgcolor: "#dbdbdb" }} />
                
                {isOrgAdmin() && (
                  <>
                    <ListItem
                      disablePadding
                      onClick={() => { handleUserMenuClose(); navigate('/org-settings'); }}
                      sx={{
                        py: 0.75,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="Organization"
                        primaryTypographyProps={{
                          sx: {
                            fontFamily: "Poppins, sans-serif",
                            fontSize: 15,
                            color: "#1a1a1a",
                            fontWeight: "normal",
                          },
                        }}
                      />
                    </ListItem>
                    <Divider sx={{ bgcolor: "#dbdbdb" }} />
                  </>
                )}
                
                {isOrgAdmin() && (
                  <>
                    <ListItem
                      disablePadding
                      onClick={() => { handleUserMenuClose(); navigate('/manage-rooms'); }}
                      sx={{
                        py: 0.75,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="Manage Rooms"
                        primaryTypographyProps={{
                          sx: {
                            fontFamily: "Poppins, sans-serif",
                            fontSize: 15,
                            color: "#1a1a1a",
                            fontWeight: "normal",
                          },
                        }}
                      />
                    </ListItem>
                    <Divider sx={{ bgcolor: "#dbdbdb" }} />
                  </>
                )}
                
                <ListItem
                  disablePadding
                  onClick={() => { handleUserMenuClose(); handleLogout(); }}
                  sx={{
                    py: 0.75,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{
                      sx: {
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 15,
                        color: "#e33629",
                        fontWeight: "normal",
                      },
                    }}
                  />
                                 </ListItem>
               </List>
           </Paper>
        </>
      )}

       {/* Organization Menu */}
       {orgMenuOpen && organizations.length > 1 && (
         <>
           {/* Backdrop */}
           <Box
             onClick={handleOrgMenuClose}
             sx={{
               position: 'fixed',
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               zIndex: theme.zIndex.modal,
               background: 'transparent',
             }}
           />
           {/* Dropdown Menu */}
           <Paper
             elevation={1}
             onMouseEnter={() => {
               if (orgHoverTimeout) {
                 clearTimeout(orgHoverTimeout);
                 setOrgHoverTimeout(null);
               }
             }}
             onMouseLeave={handleOrgMenuClose}
             sx={{
               position: 'fixed',
               top: `${orgMenuPosition.top}px`,
               left: `${orgMenuPosition.left}px`,
               width: "fit-content",
               p: '8px',
               borderRadius: 2,
               zIndex: theme.zIndex.modal + 1,
             }}
           >
             <List sx={{ pl: '12px', pt: '4px', pb: '4px', minWidth: 180 }}>
               {organizations.map((org, index) => (
                 <React.Fragment key={org._id}>
                   <ListItem
                     disablePadding
                     onClick={() => { 
                       handleOrgMenuClose(); 
                       if (org._id !== user.currentOrganization._id) {
                         handleOrganizationChange({ target: { value: org._id } });
                       }
                     }}
                     sx={{
                       py: 0.75,
                       cursor: "pointer",
                       '&:hover': {
                         bgcolor: "rgba(0, 0, 0, 0.04)",
                       },
                     }}
                   >
                     <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                       <Avatar
                         src={org.logo?.url || org.logo?.thumbnailUrl}
                         sx={{ 
                           width: 33, 
                           height: 33,
                           bgcolor: theme.palette.background.paper,
                           border: `1px solid ${theme.palette.primary.main}`,
                           fontSize: "14px",
                           color: theme.palette.primary.main,
                         }}
                       >
                         {org.name?.charAt(0).toUpperCase()}
                       </Avatar>
                       <ListItemText
                         primary={org.name}
                         primaryTypographyProps={{
                           sx: {
                             fontFamily: "Poppins, sans-serif",
                             fontSize: 15,
                             color: "#1a1a1a",
                             fontWeight: "normal",
                           },
                         }}
                       />
                     </Stack>
                   </ListItem>
                   {index < organizations.length - 1 && (
                     <Divider sx={{ bgcolor: "#dbdbdb" }} />
                   )}
                 </React.Fragment>
               ))}
             </List>
           </Paper>
         </>
       )}
    </Box>
  );
}

export default Navbar;

